import { useTranslation } from 'react-i18next';
import {
  isAgeRestrictionCase,
  isConsentRequiredCase,
  isIDValidationCase,
  isOrgAccessRestricted,
  isRequestInexistent,
  isRequestPendingApproval,
  isSubscriptionCase,
} from '../../../../../utils/utilsParking';
import { Button } from '../../../Button';
import { ButtonsSectionProps } from './props';
import UnlockIcon from '../../../../../assets/svg/unlock.svg?react';
import { checkIfCanUnlockGate } from '../../../../../utils/utilGates';

export const ButtonsSection = ({
  currentUser,
  parking,
  userSubscriptions,
  selectedSubscriptionPlan,
  selectedGate,
  ongoingSession,
  isLoadingUnlocking,
  isLoadingRequestAccess,
  isPollingForUnlockEvent,
  isSingularLayout,
  onRequestAccess,
  onSubscribeClick,
  onOpenConsentModal,
  handleUnlock,
  onReportIssue,
}: ButtonsSectionProps) => {
  const { t } = useTranslation();
  const parkingDevices = parking?.devices ?? [];
  const allGates = parkingDevices.flatMap((device) => device.gates ?? []);
  const isSingleDisabledGate =
    allGates.length === 1 && allGates[0]?.status === 'disabled';

  const gateCanUnlock = isSingularLayout
    ? allGates[0]
      ? checkIfCanUnlockGate(allGates[0], currentUser)
      : true
    : (selectedGate?.canUnlock ?? false);

  if (isRequestPendingApproval(parking)) {
    return (
      <Button disabled={true} loading={false} onClick={() => {}} type="primary">
        {t('pendingApproval')}...
      </Button>
    );
  }

  if (parking.isUnavailable) {
    return (
      <Button disabled={true} loading={false} onClick={() => {}} type="primary">
        {t('unavailable')}
      </Button>
    );
  }

  if (isOrgAccessRestricted(currentUser, parking)) {
    return (
      <Button disabled={true} loading={false} onClick={() => {}} type="primary">
        {t('accessRestricted')}
      </Button>
    );
  }

  if (isRequestInexistent(currentUser, parking)) {
    return (
      <Button
        disabled={isLoadingRequestAccess}
        loading={isLoadingRequestAccess}
        onClick={() => onRequestAccess()}
        type="primary"
      >
        {t('requestAccess')}
      </Button>
    );
  }

  if (isConsentRequiredCase(currentUser, parking)) {
    return (
      <Button
        disabled={false}
        loading={false}
        onClick={onOpenConsentModal}
        type="primary"
      >
        {t('reviewAndAcceptTerms')}
      </Button>
    );
  }

  if (isIDValidationCase(currentUser, parking)) {
    return (
      <Button disabled={true} onClick={() => {}} type="primary">
        {t('confirmIdentity')}
      </Button>
    );
  }

  if (isAgeRestrictionCase(currentUser, parking)) {
    return (
      <Button disabled={true} loading={false} onClick={() => {}} type="primary">
        {t('ageRestricted')}
      </Button>
    );
  }

  if (isSubscriptionCase(currentUser, parking, userSubscriptions)) {
    return (
      <Button
        onClick={() => onSubscribeClick()}
        type="primary"
        disabled={!selectedSubscriptionPlan}
      >
        {t('subscribeToUnlock')}
      </Button>
    );
  }

  const isBlockedByOngoingSession =
    ongoingSession !== null &&
    ongoingSession?.parking?.id !== parking.id &&
    ongoingSession?.parking?.referenceParking?.id !== parking.id;

  if (isBlockedByOngoingSession) {
    return (
      <Button disabled={true} loading={false} onClick={() => {}} type="primary">
        {t('session.errors.endSessionBeforeUnlocking')}
      </Button>
    );
  }

  if (isSingleDisabledGate) {
    return (
      <div className="flex flex-col ">
        <Button
          disabled={true}
          Icon={UnlockIcon}
          onClick={() => {}}
          type="primary"
        >
          {t('singleDisabledGate')}
        </Button>

        <button
          className="text-primary underline text-sm mt-3 mx-auto"
          onClick={onReportIssue}
        >
          {t('somethingIsWrong')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col ">
      <Button
        disabled={
          !parking?.isInUnlockRadius ||
          isLoadingUnlocking ||
          !gateCanUnlock ||
          (!isSingularLayout && !selectedGate)
        }
        loading={isPollingForUnlockEvent}
        Icon={UnlockIcon}
        onClick={handleUnlock}
        type="primary"
      >
        {parking.isInUnlockRadius
          ? `${t('unlock')} ${
              isSingularLayout
                ? ''
                : !isSingularLayout && !selectedGate
                ? ''
                : selectedGate?.label
            }`
          : t('tooFarAway')}
      </Button>
      <button
        className="text-primary underline text-sm mt-3 mx-auto"
        onClick={onReportIssue}
      >
        {t('somethingIsWrong')}
      </button>
    </div>
  );
};
